import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LANGS, splitLangPath } from './config/langs';

// Arabic and English are the primary markets, so they ship in the entry chunk
// and are ready on first paint. Russian and Chinese add ~45 KB of JSON that the
// overwhelming majority of visitors never read, so they load on demand —
// as does Urdu.
import en from './locales/en.json';
import ar from './locales/ar.json';

const lazyLocales = {
    ru: () => import('./locales/ru.json'),
    zh: () => import('./locales/zh.json'),
    ur: () => import('./locales/ur.json')
};

/**
 * The page language comes from the URL and nothing else: /en/… is English,
 * an unprefixed path is Arabic. It used to come from ?lang=, then
 * localStorage, then the browser — which meant one URL showed different
 * languages to different visitors, and Google only ever saw the Arabic one.
 * Switching language is now a navigation to another URL (see the header).
 */
export const urlLang = splitLangPath(window.location.pathname).lang;

i18n
    .use(initReactI18next)
    .init({
        lng: urlLang,
        resources: {
            en: { translation: en },
            ar: { translation: ar }
        },
        fallbackLng: 'en',
        supportedLngs: LANGS,
        load: 'languageOnly',
        interpolation: {
            escapeValue: false // react already safes from xss
        }
    });

/**
 * Fetches a lazily-bundled language and re-renders once it lands.
 *
 * Until it resolves, i18next falls back to English — visible only to Russian
 * and Chinese visitors, and only for the length of one small request.
 */
export async function ensureLanguage(lng) {
    const code = (lng || 'en').split('-')[0];
    const load = lazyLocales[code];
    if (!load || i18n.hasResourceBundle(code, 'translation')) return;

    try {
        const mod = await load();
        i18n.addResourceBundle(code, 'translation', mod.default, true, true);
        // Nudge react-i18next to re-render now that the real strings exist.
        if ((i18n.language || '').split('-')[0] === code) {
            await i18n.changeLanguage(code);
        }
    } catch (error) {
        // A failed chunk fetch is not fatal — English stays on screen.
        console.error(`Failed to load the "${code}" translations`, error);
    }
}

/**
 * Mirrors the active language onto <html lang> and <html dir>.
 *
 * This used to live only in the header's language switcher, which meant a
 * visitor detected as Arabic on first load (localStorage or browser locale,
 * without ever touching the switcher) got an LTR document — so every
 * [dir="rtl"] rule, including the Arabic typography, stayed switched off.
 */
export const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

export const isRtlLanguage = (lng) => RTL_LANGUAGES.includes((lng || '').split('-')[0]);

export function applyDocumentDirection(lng) {
    const code = (lng || 'en').split('-')[0];
    const dir = isRtlLanguage(code) ? 'rtl' : 'ltr';
    document.documentElement.lang = code;
    document.documentElement.dir = dir;
}

i18n.on('languageChanged', (lng) => {
    ensureLanguage(lng);
    applyDocumentDirection(lng);
});

// Covers the first load, where detection has already run before any switch.
ensureLanguage(i18n.language);
applyDocumentDirection(i18n.language);

export default i18n;
